import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Box, Button, Paper, Snackbar, Alert, Typography, List, ListItem, ListItemText, Divider, useMediaQuery, useTheme
} from '@mui/material';
import { useAuth } from '../../hooks/useAuth';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useLocation, useNavigate } from 'react-router-dom';
import ProcedureQuestionnaireSection from './ProcedureQuestionnaireSection';
import ProcedureVersionBadge from './ProcedureVersionBadge';
import { 
  getProcedureQuestionnaireTemplate, 
  getLatestProcedureQuestionnaireTemplate, 
  CURRENT_PROCEDURE_QUESTIONNAIRE_VERSION 
} from './procedureQuestionnaireTemplate';
import '../../styles/FHIRQuestionnaire.css';

const ProcedureForm = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
  
  // Get data from navigation state
  const { patient, procedure, admissionId, mode } = location.state || {};
  
  const [activeSection, setActiveSection] = useState(0);
  const [responses, setResponses] = useState({});
  const [status, setStatus] = useState('planned');
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [validationErrors, setValidationErrors] = useState({});
  const [templateVersion, setTemplateVersion] = useState(CURRENT_PROCEDURE_QUESTIONNAIRE_VERSION);
  const [currentTemplate, setCurrentTemplate] = useState(getLatestProcedureQuestionnaireTemplate());
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [procedureData, setProcedureData] = useState(procedure || {
    patient_id: patient?.patient_id,
    admission_id: admissionId,
    procedure_type: 'minor',
    setting: admissionId ? 'inpatient' : 'outpatient',
    status: 'planned',
    procedure_name: '',
    scheduled_date: '',
    location: '',
    primary_surgeon: '',
    assisting_staff: '',
    summary: '',
    notes: ''
  });
  
  const sections = currentTemplate.item || [];

  useEffect(() => {
    if (procedure?.procedure_id) {
      loadProcedure();
    } else {
      // For new procedures, always use latest template
      setTemplateVersion(CURRENT_PROCEDURE_QUESTIONNAIRE_VERSION);
      setCurrentTemplate(getLatestProcedureQuestionnaireTemplate());
    }
  }, [procedure?.procedure_id, mode]);

  const loadProcedure = async () => {
    if (!procedure?.procedure_id) return;
    
    try {
      setIsLoading(true);
      const { data } = await axios.get(`${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/procedures/${procedure.procedure_id}`);
      
      setProcedureData(data);
      setStatus(data.status);
      
      if (data.questionnaire_data) {
        setResponses(data.questionnaire_data);
      }
      
      // Load the appropriate template version
      const storedVersion = data.template_version;
      setTemplateVersion(storedVersion);
      
      if (storedVersion) {
        const versionedTemplate = getProcedureQuestionnaireTemplate(storedVersion);
        setCurrentTemplate(versionedTemplate);
      } else {
        const latestTemplate = getLatestProcedureQuestionnaireTemplate();
        setCurrentTemplate(latestTemplate);
        setTemplateVersion(CURRENT_PROCEDURE_QUESTIONNAIRE_VERSION);
      }
    } catch (error) {
      console.error('Error loading procedure:', error);
      setNotification({
        open: true,
        message: 'Error loading procedure data',
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResponseChange = (linkId, value) => {
    setResponses(prev => ({
      ...prev,
      [linkId]: value
    }));
    
    // Clear validation error when user provides input
    if (validationErrors[linkId]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[linkId];
        return newErrors;
      });
    }
  };

  const handleProcedureDataChange = (field, value) => {
    setProcedureData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateSection = (section) => {
    const errors = {};
    let isValid = true;
    let hasAnyContent = false;
    
    const validateItems = (items) => {
      items.forEach(item => {
        if (item.type === 'group') {
          validateItems(item.item || []);
        } else {
          // Check for required fields
          if (item.required && (!responses[item.linkId] || responses[item.linkId] === '')) {
            errors[item.linkId] = `${item.text} is required`;
            isValid = false;
          }
          // Check if there's any content in this field
          if (responses[item.linkId] && responses[item.linkId].toString().trim() !== '') {
            hasAnyContent = true;
          }
        }
      });
    };
    
    validateItems(section.item || []);
    
    // Special validation for sections that should have content but don't have required fields
    if (isValid && !hasAnyContent) {
      // For consumables and post-op sections, require at least some content to mark as complete
      const sectionId = section.linkId;
      if (sectionId === 'intraOpConsumables' || sectionId === 'postOperative') {
        isValid = false;
      }
    }
    
    return { isValid, errors };
  };

  const validateProcedureData = () => {
    const errors = {};
    let isValid = true;
    
    if (!procedureData.procedure_name?.trim()) {
      errors.procedure_name = 'Procedure name is required';
      isValid = false;
    }
    
    if (!procedureData.procedure_type) {
      errors.procedure_type = 'Procedure type is required';
      isValid = false;
    }
    
    if (!procedureData.setting) {
      errors.setting = 'Setting is required';
      isValid = false;
    }
    
    return { isValid, errors };
  };

  const saveProcedure = async (saveType = 'draft') => {
    try {
      setIsSaving(true);
      
      // If we're in questionnaire mode, only validate and save questionnaire data
      if (mode === 'questionnaire') {
        const currentSection = sections[activeSection];
        const { isValid, errors } = validateSection(currentSection);
        
        if (!isValid && saveType === 'complete') {
          setValidationErrors(errors);
          setNotification({
            open: true,
            message: 'Please fill in all required fields in this section',
            severity: 'error'
          });
          return;
        }
        
        // Save questionnaire data
        await axios.patch(
          `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/procedures/${procedure.procedure_id}/questionnaire`,
          {
            questionnaire_data: responses,
            template_version: templateVersion,
            status: saveType === 'complete' ? 'completed' : status
          }
        );
      } else {
        // Validate procedure data for new procedures
        const { isValid: procedureValid, errors: procedureErrors } = validateProcedureData();
        
        if (!procedureValid) {
          setValidationErrors(procedureErrors);
          setNotification({
            open: true,
            message: 'Please fill in all required procedure information',
            severity: 'error'
          });
          return;
        }
        
        const procedurePayload = {
          ...procedureData,
          questionnaire_data: responses,
          template_version: templateVersion || CURRENT_PROCEDURE_QUESTIONNAIRE_VERSION
        };
        
        if (procedure?.procedure_id) {
          // Update existing procedure
          await axios.put(
            `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/procedures/${procedure.procedure_id}`,
            procedurePayload
          );
        } else {
          // Create new procedure
          const response = await axios.post(
            `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/procedures`,
            procedurePayload
          );
          setProcedureData(response.data);
        }
      }
      
      setNotification({
        open: true,
        message: saveType === 'complete' ? 'Procedure completed successfully' : 'Procedure saved successfully',
        severity: 'success'
      });
      
      if (saveType === 'complete') {
        setStatus('completed');
        setTimeout(() => {
          navigate(-1);
        }, 1000);
      }
    } catch (error) {
      console.error('Error saving procedure:', error);
      setNotification({
        open: true,
        message: error.response?.data?.error || 'Error saving procedure',
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSectionComplete = (sectionIndex) => {
    const section = sections[sectionIndex];
    const { isValid, errors } = validateSection(section);
    
    if (isValid) {
      saveProcedure('draft');
      if (sectionIndex < sections.length - 1) {
        setActiveSection(sectionIndex + 1);
      }
    } else {
      setValidationErrors(errors);
      setNotification({
        open: true,
        message: 'Please fill in all required fields before proceeding',
        severity: 'error'
      });
    }
  };

  const getSectionCompletionStatus = (sectionIndex) => {
    const section = sections[sectionIndex];
    const { isValid } = validateSection(section);
    return isValid;
  };

  const onClose = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading procedure...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {/* Sidebar - Section Navigation */}
      <Paper 
        elevation={2} 
        sx={{ 
          width: 320, 
          overflow: 'auto',
          borderRadius: 0
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            {procedure?.procedure_id ? 'Edit Procedure' : 'New Procedure'}
          </Typography>
          
          {patient && (
            <Box sx={{ mb: 2, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Patient:</strong> {patient.patient_first_name} {patient.patient_last_name}
              </Typography>
              <Typography variant="body2">
                <strong>Patient #:</strong> {patient.patient_number}
              </Typography>
            </Box>
          )}

          {/* Procedure Basic Info Form - only show if not in questionnaire mode */}
          {mode !== 'questionnaire' && (
            <Box sx={{ mb: 3, p: 2, border: 1, borderColor: 'grey.300', borderRadius: 1 }}>
              <Typography variant="subtitle1" gutterBottom>Procedure Information</Typography>
              
              <Box sx={{ mb: 2 }}>
                <label>Procedure Name *</label>
                <input
                  type="text"
                  value={procedureData.procedure_name || ''}
                  onChange={(e) => handleProcedureDataChange('procedure_name', e.target.value)}
                  style={{ 
                    width: '100%', 
                    padding: '8px', 
                    marginTop: '4px',
                    border: validationErrors.procedure_name ? '1px solid red' : '1px solid #ccc',
                    borderRadius: '4px'
                  }}
                />
                {validationErrors.procedure_name && (
                  <Typography variant="caption" color="error">{validationErrors.procedure_name}</Typography>
                )}
              </Box>

              <Box sx={{ mb: 2 }}>
                <label>Type *</label>
                <select
                  value={procedureData.procedure_type || ''}
                  onChange={(e) => handleProcedureDataChange('procedure_type', e.target.value)}
                  style={{ 
                    width: '100%', 
                    padding: '8px', 
                    marginTop: '4px',
                    border: validationErrors.procedure_type ? '1px solid red' : '1px solid #ccc',
                    borderRadius: '4px'
                  }}
                >
                  <option value="">Select Type</option>
                  <option value="major">Major</option>
                  <option value="minor">Minor</option>
                </select>
                {validationErrors.procedure_type && (
                  <Typography variant="caption" color="error">{validationErrors.procedure_type}</Typography>
                )}
              </Box>

              <Box sx={{ mb: 2 }}>
                <label>Setting *</label>
                <select
                  value={procedureData.setting || ''}
                  onChange={(e) => handleProcedureDataChange('setting', e.target.value)}
                  style={{ 
                    width: '100%', 
                    padding: '8px', 
                    marginTop: '4px',
                    border: validationErrors.setting ? '1px solid red' : '1px solid #ccc',
                    borderRadius: '4px'
                  }}
                >
                  <option value="">Select Setting</option>
                  <option value="inpatient">Inpatient</option>
                  <option value="outpatient">Outpatient</option>
                </select>
                {validationErrors.setting && (
                  <Typography variant="caption" color="error">{validationErrors.setting}</Typography>
                )}
              </Box>

              <Box sx={{ mb: 2 }}>
                <label>Scheduled Date</label>
                <input
                  type="datetime-local"
                  value={procedureData.scheduled_date || ''}
                  onChange={(e) => handleProcedureDataChange('scheduled_date', e.target.value)}
                  style={{ 
                    width: '100%', 
                    padding: '8px', 
                    marginTop: '4px',
                    border: '1px solid #ccc',
                    borderRadius: '4px'
                  }}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <label>Primary Surgeon</label>
                <input
                  type="text"
                  value={procedureData.primary_surgeon || ''}
                  onChange={(e) => handleProcedureDataChange('primary_surgeon', e.target.value)}
                  style={{ 
                    width: '100%', 
                    padding: '8px', 
                    marginTop: '4px',
                    border: '1px solid #ccc',
                    borderRadius: '4px'
                  }}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <label>Location</label>
                <input
                  type="text"
                  value={procedureData.location || ''}
                  onChange={(e) => handleProcedureDataChange('location', e.target.value)}
                  style={{ 
                    width: '100%', 
                    padding: '8px', 
                    marginTop: '4px',
                    border: '1px solid #ccc',
                    borderRadius: '4px'
                  }}
                />
              </Box>
            </Box>
          )}

          <ProcedureVersionBadge version={templateVersion} />
          
          <Divider sx={{ my: 2 }} />
          
          <List dense>
            {sections.map((section, index) => (
              <ListItem 
                key={section.linkId}
                button
                selected={activeSection === index}
                onClick={() => setActiveSection(index)}
                sx={{ borderRadius: 1, mb: 0.5 }}
              >
                <Box sx={{ mr: 1 }}>
                  {getSectionCompletionStatus(index) ? (
                    <CheckCircleIcon color="success" fontSize="small" />
                  ) : (
                    <RadioButtonUncheckedIcon color="disabled" fontSize="small" />
                  )}
                </Box>
                <ListItemText 
                  primary={section.text} 
                  primaryTypographyProps={{ fontSize: '0.9rem' }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Paper>

      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Paper elevation={1} sx={{ p: 2, borderRadius: 0 }}>
          {/* User Signature Section */}
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'info.light', borderRadius: 1, border: 1, borderColor: 'info.main' }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'info.contrastText' }}>
              📝 Electronic Documentation by: {user?.name || 'Unknown User'} ({user?.role || 'Unknown Role'})
            </Typography>
            <Typography variant="caption" sx={{ color: 'info.contrastText', fontStyle: 'italic' }}>
              This electronic entry serves as your professional signature and attestation to the accuracy of the documented information.
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ color: 'text.secondary' }}>
              Procedure Documentation
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                variant="outlined" 
                onClick={onClose}
                disabled={isSaving}
              >
                Close
              </Button>
              <Button 
                variant="contained" 
                onClick={() => saveProcedure('draft')}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Draft'}
              </Button>
              {activeSection === sections.length - 1 && (
                <Button 
                  variant="contained" 
                  color="success"
                  onClick={() => saveProcedure('complete')}
                  disabled={isSaving}
                >
                  Complete Procedure
                </Button>
              )}
            </Box>
          </Box>
        </Paper>

        {/* Section Content */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
          {sections[activeSection] && (
            <ProcedureQuestionnaireSection
              section={sections[activeSection]}
              responses={responses}
              onResponseChange={handleResponseChange}
              validationErrors={validationErrors}
              onSectionComplete={() => handleSectionComplete(activeSection)}
              isLastSection={activeSection === sections.length - 1}
              disabled={status === 'completed' && mode === 'questionnaire'}
            />
          )}
        </Box>
      </Box>

      {/* Notifications */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert severity={notification.severity} onClose={() => setNotification({ ...notification, open: false })}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProcedureForm;
